import React, { useState, useEffect, useMemo } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import SweetAlert from "react-bootstrap-sweetalert";
import redirectURL from "../redirectURL";
import "../../assets/css/inventorymanagement.css";
import CountUp from "react-countup";
import { LuPackage2 } from "react-icons/lu";
import { PiPackageBold } from "react-icons/pi";
import "./dashboard.css";

// Register ChartJS components for all chart types
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);


const CurrentStockDashboard = () => {
    const [loadshow, setLoadshow] = useState("show-n");
    const [overlayBlock, setOverlayBlock] = useState("show-n");
    // Then in your component, add this state (near your other states like currentPage)
    const [chartType, setChartType] = useState('bar');
    const [showGridView, setShowGridView] = useState(true);
    const [show, setShow] = useState(false);
    const [basicTitle, setBasicTitle] = useState("");
    const [basicType, setBasicType] = useState("default");
    const [chartData, setChartData] = useState(null);
    const [darkstoreCounters, setDarkstoreCounters] = useState([]);
    const [warehouseCounters, setWarehouseCounters] = useState([]);

    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 15;

    const [columnDefs, setColumnDefs] = useState([]);


    const [rowData, setRowData] = useState([]);
    const [rowDataWarehouse, setRowDataWarehouse] = useState([]);

    // Add this state for counter selection
    const [selectedCounter, setSelectedCounter] = useState(null);
    const [filteredRowData, setFilteredRowData] = useState([]);

    // Function to handle counter selection
    const handleCounterSelect = (type, index, counter) => {
        const newSelection = { type, index, counter };
        setSelectedCounter(newSelection);

        // Filter row data based on selected counter
        filterGridData(newSelection);
    };

    // Function to filter grid data based on counter selection
    // Function to filter grid data based on counter selection
    const filterGridData = (selection) => {
        if (!selection || !selection.counter) {
            setFilteredRowData([]);
            return;
        }

        var selectedValue = "";
        if (selection.type === "warehouse") {
            selectedValue = selection.counter.warehouse_id;
        }
        if (selection.type === "darkstore") {
            selectedValue = selection.counter.dark_store_id;
        }

        var filteredData = [];
        if (selection.type === "warehouse" && rowDataWarehouse && rowDataWarehouse.length > 0) {
            filteredData = rowDataWarehouse.filter(row => row.warehouse_id === selectedValue);
        }
        if (selection.type === "darkstore" && rowData && rowData.length > 0) {
            filteredData = rowData.filter(row => row.dark_store_id === selectedValue);
        }

        // console.log(`Filtered ${filteredData.length} rows for ${selection.type}`, filteredData);
        setFilteredRowData(filteredData.length > 0 ? filteredData : []);
    };

    // Function to generate chart data from filteredRowData
    const generateChartData = (data, startIndex = 0, itemsPerPage = 15) => {
        if (!data || data.length === 0) {
            return null;
        }

        // Group by SKU and sum current stock
        const skuStockMap = {};
        data.forEach(item => {
            const sku = item.sku_code || item.sku || 'Unknown SKU';
            const stock = item.total_inventory || item.cls_bal || 0;

            if (skuStockMap[sku]) {
                skuStockMap[sku] += Number(stock);
            } else {
                skuStockMap[sku] = Number(stock);
            }
        });

        // Sort by stock quantity
        const sortedSKUs = Object.entries(skuStockMap)
            .sort(([, a], [, b]) => b - a);

        // Get paginated SKUs
        const paginatedSKUs = sortedSKUs.slice(startIndex, startIndex + itemsPerPage);

        const labels = paginatedSKUs.map(([sku]) => sku);
        const stockData = paginatedSKUs.map(([, stock]) => stock);

        // Generate colors for the datasets
        // const backgroundColors = [
        //     '#E3578F', '#BA386E', '#93385D', '#6C2846', 
        //     '#FF6B9D', '#FF85AE', '#FF9FBF', '#FFB8D0',
        //     '#4ECDC4', '#45B7AA', '#3DA191', '#358B7D',
        //     '#FFE66D', '#FFDA33', '#FFCE00', '#FFC100'
        // ];

        const backgroundColors = [
            '#BA386E', // Primary brand color
            '#E3578F', // Lighter pink
            '#93385D', // Darker maroon
            '#D2B2BF', // Secondary light pink
            '#A82C5A', // Dark pink
            '#F06BA0', // Bright pink
            '#8B3052', // Deep wine
            '#E8C8D4', // Very light pink
            '#C94A7C', // Medium pink
            '#7D2647', // Dark wine
            '#F7A8C8', // Soft pink
            '#6C1F3B', // Very dark wine
            '#FFC2D9', // Pale pink
            '#9E3B65', // Muted pink
            '#EF91B7', // Light warm pink
            '#B54A7A'  // Medium warm pink
        ];

        return {
            labels,
            datasets: [
                {
                    label: 'Current Stock',
                    data: stockData,
                    backgroundColor: backgroundColors.slice(0, stockData.length),
                    borderColor: '#fff',
                    borderWidth: 2,
                    borderRadius: 4,
                },
            ],
            totalSKUs: sortedSKUs.length, // For pagination info
            currentPage: Math.floor(startIndex / itemsPerPage) + 1,
            totalPages: Math.ceil(sortedSKUs.length / itemsPerPage)
        };
    };

    // Initialize column definitions when data first loads
    useEffect(() => {
        if (rowData && rowData.length > 0 && columnDefs.length === 0) {
            const newColumnDefs = generateColumnDefs(rowData);
            setColumnDefs(newColumnDefs);
        }
    }, [rowData]);

    // Update filtered data and column definitions when selection or data changes
    useEffect(() => {
        if (selectedCounter) {
            filterGridData(selectedCounter);

            // Generate column definitions based on the selected data source
            let dataSource = [];
            if (selectedCounter.type === 'darkstore') {
                dataSource = rowData;
            } else if (selectedCounter.type === 'warehouse') {
                dataSource = rowDataWarehouse;
            }

            if (dataSource && dataSource.length > 0) {
                const newColumnDefs = generateColumnDefs(dataSource);
                setColumnDefs(newColumnDefs);
            }
        } else {
            setFilteredRowData(rowData);
            // Set default column definitions based on darkstore data
            if (rowData && rowData.length > 0) {
                const newColumnDefs = generateColumnDefs(rowData);
                setColumnDefs(newColumnDefs);
            }
        }
    }, [selectedCounter, rowData, rowDataWarehouse]);

    useEffect(() => {
        if (filteredRowData && filteredRowData.length > 0) {
            const chartData = generateChartData(filteredRowData, currentPage * itemsPerPage, itemsPerPage);
            setChartData(chartData);
        } else {
            setChartData(null);
        }
    }, [filteredRowData, currentPage, itemsPerPage]); // Add currentPage and itemsPerPage to dependencies

    // Reset current page when counter changes
    useEffect(() => {
        setCurrentPage(0);
    }, [selectedCounter]);

    // Reset current page when chart type changes
    useEffect(() => {
        setCurrentPage(0);
    }, [chartType]);

    // Set first counter as default selection when data loads
    // Set first counter as default selection when data loads - FIXED VERSION
    useEffect(() => {
        // Only auto-select if we have counters but no current selection
        if (!selectedCounter) {
            if (darkstoreCounters.length > 0) {
                // Small delay to ensure rowData is set
                setTimeout(() => {
                    handleCounterSelect('darkstore', 0, darkstoreCounters[0]);
                }, 100);
            } else if (warehouseCounters.length > 0) {
                setTimeout(() => {
                    handleCounterSelect('warehouse', 0, warehouseCounters[0]);
                }, 100);
            }
        }
    }, [darkstoreCounters, warehouseCounters]); // Removed rowData dependencies to avoid loops

    // Function to generate column definitions dynamically with field filtering and renaming
    const generateColumnDefs = (data) => {
        // console.log(selectedCounter, "selectedCounter");

        if (!data || data.length === 0) {
            return [];
        }

        // Get all unique keys from the first few rows
        const allKeys = new Set();
        data.slice(0, 10).forEach(row => {
            Object.keys(row).forEach(key => allKeys.add(key));
        });

        // Fields to hide
        const hiddenFields = ['_id', 'created_date', 'process', 'created_at', 'updated_date', 'image', 'sr', 'not_found', 'updated', 'uploaded_date', 'filename', 'updated_at', '__v', 'blocked_committed', 'pendency_on_all_channels'];

        // Create column definitions
        const generatedColumnDefs = Array.from(allKeys)
            .filter(key => !hiddenFields.includes(key)) // Filter out hidden fields
            .map(key => ({
                field: key,
                headerName: formatHeaderName(key),
                filter: true,
                sortable: true,
                resizable: true,
                minWidth: getColumnWidth(key),
                flex: 1,
                valueFormatter: getValueFormatter(key),
                cellStyle: getCellStyle(key),
            }));

        return generatedColumnDefs;
    };

    // Helper functions
    const formatHeaderName = (key) => {
        // Handle date format fields like "6sep24_in"
        if (isDateField(key)) {
            return formatDateFieldHeader(key);
        }

        // Regular field name formatting
        return key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    const isDateField = (key) => {
        // Match patterns like: 6sep24_in, 15jan25_out, 1dec24_inventory, etc.
        const datePattern = /^\d{1,2}[a-z]{3,4}\d{2,4}/i;
        return datePattern.test(key);
    };

    const formatDateFieldHeader = (key) => {
        // Extract date part and suffix
        const match = key.match(/^(\d{1,2})([a-z]{3,4})(\d{2,4})_?(.*)$/i);
        if (!match) return formatHeaderName(key);

        const [, day, month, year, suffix] = match;

        // Convert month abbreviation to full month name
        const monthNames = {
            jan: 'January', feb: 'February', mar: 'March', apr: 'April',
            may: 'May', jun: 'June', jul: 'July', aug: 'August',
            sep: 'September', oct: 'October', nov: 'November', dec: 'December'
        };

        const fullMonth = monthNames[month.toLowerCase()] || month;

        // Format year (handle 2-digit and 4-digit years)
        const fullYear = year.length === 2 ? `20${year}` : year;

        // Format the header
        let header = `${parseInt(day)} ${fullMonth} ${fullYear}`;
        if (suffix) {
            header += ` ${suffix.toUpperCase()}`;
        }

        return header;
    };

    const getValueFormatter = (key) => {
        if (isDateField(key)) {
            return dateValueFormatter;
        } else if (isNumericColumn(key)) {
            return numberFormatter;
        } else if (isDateColumn(key)) {
            return dateFormatter;
        }
        return null;
    };

    const getCellStyle = (key) => {
        if (isNumericColumn(key)) {
            return { textAlign: 'right' };
        }
        return null;
    };

    const getColumnWidth = (key) => {
        const wideColumns = ['description', 'name', 'address', 'remarks', 'title'];
        const narrowColumns = ['id', 'status', 'code', 'type', 'qty', 'quantity'];

        if (isDateField(key)) {
            return 150; // Fixed width for date fields
        } else if (wideColumns.some(wide => key.toLowerCase().includes(wide))) {
            return 200;
        } else if (narrowColumns.some(narrow => key.toLowerCase().includes(narrow))) {
            return 100;
        }
        return 150;
    };

    const isDateColumn = (key) => {
        const dateKeywords = ['date', 'time', 'created', 'updated', 'timestamp'];
        return dateKeywords.some(keyword => key.toLowerCase().includes(keyword));
    };

    const isNumericColumn = (key) => {
        const numericKeywords = ['count', 'quantity', 'amount', 'price', 'total', 'value', 'number', 'qty', 'stock', 'inventory'];
        return numericKeywords.some(keyword => key.toLowerCase().includes(keyword));
    };

    // Formatter functions
    const dateValueFormatter = (params) => {
        if (!params.value) return '';
        // For date fields, just return the value as is or format if needed
        return params.value;
    };

    const dateFormatter = (params) => {
        if (!params.value) return '';
        try {
            // Handle various date formats
            const date = new Date(params.value);
            return isNaN(date.getTime()) ? params.value : date.toLocaleDateString();
        } catch {
            return params.value;
        }
    };

    const numberFormatter = (params) => {
        if (params.value === null || params.value === undefined) return '';
        const num = Number(params.value);
        return isNaN(num) ? params.value : num.toLocaleString();
    };

    const sideBar = useMemo(
        () => ({
            toolPanels: [
                {
                    id: "columns",
                    labelDefault: "Columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel",
                },
                {
                    id: "filters",
                    labelDefault: "Filters",
                    iconKey: "filter",
                    toolPanel: "agFiltersToolPanel",
                },
            ],
        }),
        []
    );

    const statusPanel = {
        statusPanels: [
            { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
            { statusPanel: "agTotalRowCountComponent", align: "center" },
            { statusPanel: "agFilteredRowCountComponent" },
            { statusPanel: "agSelectedRowCountComponent" },
            { statusPanel: "agAggregationComponent" },
        ],
    };

    // ---------------- helpers ----------------
    const pad2 = (n) => String(n).padStart(2, "0");
    const todayYMD = () => {
        const d = new Date();
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    };
    const [stnDate, setStnDate] = useState(todayYMD());

    const closeAlert = () => setShow(false);

    const dateLabel = { fontSize: "13px", fontWeight: 400 };
    const dateInput = {
        padding: "7px",
        borderRadius: "5px",
        border: "1px solid #d9d9d9",
        width: "100%",
        marginBottom: "0px",
        cursor: "pointer",
    };
    const applyBtn = {
        backgroundColor: "#93385D",
        color: "#fff",
        borderRadius: "5px",
        padding: "8px 20px",
        border: "none",
        cursor: "pointer",
        marginRight: "20px",
        marginTop: "5px"
    };
    const resetBtn = {
        backgroundColor: "#fff",
        color: "#000",
        borderRadius: "5px",
        padding: "8px 20px",
        border: "1px solid #d9d9d9",
        cursor: "pointer",
    };

    const defaultColDef = useMemo(
        () => ({ sortable: true, filter: true, resizable: true, flex: 1, minWidth: 150 }),
        []
    );

    const fetchData = async (dateStr) => {
        setLoadshow("show-m");
        setOverlayBlock("show-m");
        try {
            const payload = { date: dateStr || stnDate };
            const resp = await redirectURL.post("inventory/getCurrentStockDashboardData", payload);

            if (!resp || !resp.data) throw new Error("No response from server");

            const status = String(resp.data.status || "").toLowerCase();
            if (status === "fail") throw new Error(resp.data.message || "Server returned failure");
            if (status !== "success") throw new Error("Invalid response format from server");

            const messageData = resp.data.data || {};

            // Update the date picker to show the actual date from which data was retrieved
            if (messageData.actual_data_date && messageData.actual_data_date !== dateStr) {
                setStnDate(messageData.actual_data_date);
            }

            // Dynamic counters
            const darkstoreCounters = JSON.parse(messageData.darkstores_counters) || [];
            const warehouseCounters = JSON.parse(messageData.warehouses_counters) || [];

            // FIRST set the data
            setRowData(JSON.parse(messageData.darkstores_data) || []);
            setRowDataWarehouse(JSON.parse(messageData.warehouses_data) || []);

            // THEN set the counters
            setDarkstoreCounters(darkstoreCounters);
            setWarehouseCounters(warehouseCounters);

            // Reset selection
            setSelectedCounter(null);
            setFilteredRowData([]);
            setChartData(null);

        } catch (e) {
            setBasicTitle("Failed to fetch STN Dashboard: " + (e.message || "Unknown error"));
            setBasicType("error");
            setShow(true);
        } finally {
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        }
    };

    const fetchDataOnApply = async (dateStr) => {
        setLoadshow("show-m");
        setOverlayBlock("show-m");
        try {
            const payload = { date: dateStr || stnDate };
            const resp = await redirectURL.post("inventory/getCurrentStockDashboardDataOnApply", payload);

            if (!resp || !resp.data) throw new Error("No response from server");

            const status = String(resp.data.status || "").toLowerCase();
            if (status === "fail") throw new Error(resp.data.message || "Server returned failure");
            if (status !== "success") throw new Error("Invalid response format from server");

            const messageData = resp.data.data || {};

            // Dynamic counters
            const darkstoreCounters = JSON.parse(messageData.darkstores_counters) || [];
            const warehouseCounters = JSON.parse(messageData.warehouses_counters) || [];

            // FIRST set the data
            setRowData(JSON.parse(messageData.darkstores_data) || []);
            setRowDataWarehouse(JSON.parse(messageData.warehouses_data) || []);

            // THEN set the counters
            setDarkstoreCounters(darkstoreCounters);
            setWarehouseCounters(warehouseCounters);

            // Reset selection to force re-selection with new data
            setSelectedCounter(null);
            setFilteredRowData([]);
            setChartData(null);

        } catch (e) {
            setBasicTitle("Failed to fetch STN Dashboard: " + (e.message || "Unknown error"));
            setBasicType("error");
            setShow(true);
        } finally {
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        }
    };

    useEffect(() => {
        fetchData(stnDate);
    }, []);


    return (
        <>
            <div className="container-fluid mt-3">
                <div style={{ zIndex: "9999999" }}>
                    <SweetAlert
                        show={show}
                        type={basicType}
                        title={basicTitle}
                        onConfirm={closeAlert}
                        style={{ fontSize: "9px" }}
                    />
                </div>

                <div className="row" style={{ fontSize: "0.9rem", fontFamily: "Arial, sans-serif" }}>

                    {/* <div className="col-sm-12 p-0">
                        <h1
                            className="col-sm-12"
                            style={{
                                color: "#B9366D",
                                fontSize: "18px",
                                fontWeight: "bold",
                                fontFamily: "sans-serif",
                            }}
                        >
                            Current Stock Dashboard
                        </h1>
                    </div> */}

                    <div className="col-sm-8 row pl-1 mt-2 mb-2" style={{ display: "flex", alignItems: "center" }}>
                        <div className="col-3">
                            <label htmlFor="stnDate" style={dateLabel}>Current Stock Date:</label>
                            <input
                                type="date"
                                id="stnDate"
                                value={stnDate}
                                onChange={(e) => setStnDate(e.target.value)}
                                onClick={() => document.getElementById("stnDate").showPicker?.()}
                                style={dateInput}
                                max={todayYMD()}
                            />
                        </div>

                        <div className="col-6 mt-4">
                            <button type="button" onClick={(e) => { e.preventDefault(); fetchDataOnApply(stnDate); }} style={applyBtn}>
                                Apply
                            </button>

                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const d = todayYMD();
                                    setStnDate(d);
                                    fetchData(d);
                                }}
                                style={resetBtn}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* Darkstores Counters Card */}
                {darkstoreCounters.length > 0 && (
                    <div
                        className="card-section"
                        style={{
                            background: "#fff",
                            padding: "15px",
                            borderRadius: "8px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                            width: "100%",
                            margin: "0 0 20px 0",
                            marginLeft: "0",
                            marginTop: "10px",
                            marginRight: "0",
                        }}
                    >
                        <div className="section-title" style={{ fontWeight: "bold", marginBottom: "15px", fontSize: "16px", color: "#333" }}>
                            Darkstores <span style={{ fontSize: "13px", color: "#ba386e", fontWeight: "normal", fontStyle: "italic" }}>(Showing Total Current Stock)</span>
                        </div>
                        <div
                            className="card-grid"
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                margin: 0,
                                gap: "12px",
                                rowGap: "15px",
                                width: "100%",
                                justifyContent: "flex-start", // Add this to align items to the start
                            }}
                        >
                            {darkstoreCounters.map((counter, index) => (
                                <div
                                    key={index}
                                    className="card-box card-darkstore"
                                    style={{
                                        // flex: "1 0 calc(25% - 12px)",
                                        flex: "0 0 calc(25% - 9px)",
                                        minWidth: "180px",
                                        maxWidth: "calc(25% - 12px)",
                                        margin: 0,
                                        boxSizing: "border-box",
                                        padding: "15px",
                                        borderRadius: "8px",
                                        border: "1px solid #e0e0e0",
                                        backgroundColor: selectedCounter?.type === 'darkstore' && selectedCounter?.index === index ? "#F7EEF2" : "#f8f9fa",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        textAlign: "center",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                    }}
                                    onClick={() => handleCounterSelect('darkstore', index, counter)}
                                    onMouseOver={(e) => {
                                        if (!(selectedCounter?.type === 'darkstore' && selectedCounter?.index === index)) {
                                            e.currentTarget.style.backgroundColor = "#f0f0f0";
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (!(selectedCounter?.type === 'darkstore' && selectedCounter?.index === index)) {
                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                        }
                                    }}
                                >
                                    <LuPackage2 className="card-icon" style={{ color: "#BA386E" }} />
                                    <div
                                        className="card-label"
                                        style={{
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            marginBottom: "8px",
                                            color: selectedCounter?.type === 'darkstore' && selectedCounter?.index === index ? "#BA386E" : "#555",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}
                                    >
                                        {counter.dark_store_name || `Counter ${index + 1}`}
                                    </div>
                                    <div
                                        className="card-value"
                                        style={{
                                            fontSize: "18px",
                                            fontWeight: "bold",
                                            color: selectedCounter?.type === 'darkstore' && selectedCounter?.index === index ? "#BA386E" : "#333"
                                        }}
                                    >
                                        <CountUp start={0} end={counter.total_current_stock || 0} duration={1.5} separator="," />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Warehouses Counters Card */}
                {warehouseCounters.length > 0 && (
                    <div
                        className="card-section"
                        style={{
                            background: "#fff",
                            padding: "15px",
                            borderRadius: "8px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                            width: "100%",
                            margin: "0 0 20px 0",
                            marginLeft: "0",
                            marginRight: "0",
                        }}
                    >
                        <div className="section-title" style={{ fontWeight: "bold", marginBottom: "15px", fontSize: "16px", color: "#333" }}>
                            Warehouses <span style={{ fontSize: "13px", color: "#ba386e", fontWeight: "normal", fontStyle: "italic" }}>(Showing Total Current Stock)</span>
                        </div>
                        <div
                            className="card-grid"
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                margin: 0,
                                gap: "12px",
                                rowGap: "15px",
                                width: "100%",
                                justifyContent: "flex-start", // Add this to align items to the start
                            }}
                        >
                            {warehouseCounters.map((counter, index) => (
                                <div
                                    key={index}
                                    className="card-box card-warehouse"
                                    style={{
                                        // flex: "1 0 calc(25% - 12px)",
                                        flex: "0 0 calc(25% - 9px)", // Changed to 0 0 for fixed basis, reduced gap calculation
                                        minWidth: "180px",
                                        maxWidth: "calc(25% - 12px)",
                                        margin: 0,
                                        boxSizing: "border-box",
                                        padding: "15px",
                                        borderRadius: "8px",
                                        border: "1px solid #e0e0e0",
                                        backgroundColor: selectedCounter?.type === 'warehouse' && selectedCounter?.index === index ? "#F7EEF2" : "#f8f9fa",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        textAlign: "center",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                    }}
                                    onClick={() => handleCounterSelect('warehouse', index, counter)}
                                    onMouseOver={(e) => {
                                        if (!(selectedCounter?.type === 'warehouse' && selectedCounter?.index === index)) {
                                            e.currentTarget.style.backgroundColor = "#f0f0f0";
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        if (!(selectedCounter?.type === 'warehouse' && selectedCounter?.index === index)) {
                                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                                        }
                                    }}
                                >
                                    <PiPackageBold className="card-icon" style={{ color: "#BA386E" }} />
                                    <div
                                        className="card-label"
                                        style={{
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            marginBottom: "8px",
                                            color: selectedCounter?.type === 'warehouse' && selectedCounter?.index === index ? "#BA386E" : "#555",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}
                                    >
                                        {counter.warehouse_name || `Counter ${index + 1}`}
                                    </div>
                                    <div
                                        className="card-value"
                                        style={{
                                            fontSize: "18px",
                                            fontWeight: "bold",
                                            color: selectedCounter?.type === 'warehouse' && selectedCounter?.index === index ? "#BA386E" : "#333"
                                        }}
                                    >
                                        <CountUp start={0} end={counter.total_current_stock || 0} duration={1.5} separator="," />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Grid section - Show when a counter is selected */}
                {/* Grid and Chart section - Show when a counter is selected */}
                {/* Toggle Button Section */}
                {selectedCounter && (
                    <div className="row">
                        <div className="col-sm-12 p-0 pl-2 mt-4">
                            <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginBottom: "15px",
                                alignItems: "center",
                                gap: "10px"
                            }}>
                                <label htmlFor="view-select" style={{
                                    fontSize: "14px",
                                    color: "#666",
                                    marginTop: "6px",
                                    fontWeight: "bold"
                                }}>
                                    Choose View:
                                </label>
                                <select
                                    id="view-select"
                                    value={showGridView ? "table" : "chart"}
                                    onChange={(e) => setShowGridView(e.target.value === "table")}
                                    style={{
                                        padding: "8px 12px",
                                        paddingRight: "32px",
                                        borderRadius: "5px",
                                        border: "1px solid #d1d5db",
                                        backgroundColor: "#fff",
                                        color: "#333",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        fontWeight: "500",
                                        minWidth: "140px",
                                        appearance: "none",
                                        backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23666' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>")`,
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "right 12px center",
                                        backgroundSize: "8px 10px",
                                        transition: "all 0.2s ease",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                                    }}
                                    onMouseOver={(e) => {
                                        e.target.style.borderColor = "#BA386E";
                                        e.target.style.boxShadow = "0 1px 3px rgba(186, 56, 110, 0.2)";
                                    }}
                                    onMouseOut={(e) => {
                                        e.target.style.borderColor = "#d1d5db";
                                        e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = "#BA386E";
                                        e.target.style.boxShadow = "0 0 0 2px rgba(186, 56, 110, 0.2)";
                                        e.target.style.outline = "none";
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = "#d1d5db";
                                        e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
                                    }}
                                >
                                    <option value="table">Grid View</option>
                                    <option value="chart">Chart View</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
                {/* Full Width Chart/Grid Section - Show when a counter is selected */}
                {/* Main Data Display Section */}
                {selectedCounter ? (
                    /* Show Chart/Grid when a counter is selected */
                    !showGridView ? (
                        /* Chart View - Full Width - Only show if chartData exists */
                        chartData ? (
                            <div className="card-body" style={{ padding: "20px", backgroundColor: "white", height: "100%", display: "flex", flexDirection: "column" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                    <h5 style={{ fontWeight: "600", marginTop: "18px", marginLeft: "14px", color: "#333" }}>
                                        Current Stock Distribution by SKU - {selectedCounter.counter.dark_store_name || selectedCounter.counter.warehouse_name}
                                    </h5>

                                    {/* Chart Type Dropdown */}
                                    <div style={{ marginRight: "14px", marginTop: "18px" }}>
                                        <label htmlFor="chart-type" style={{ marginRight: "8px", fontWeight: "500", color: "#333" }}>
                                            Chart Type:
                                        </label>
                                        <select
                                            id="chart-type"
                                            value={chartType}
                                            onChange={(e) => setChartType(e.target.value)}
                                            style={{
                                                padding: "6px 12px",
                                                border: "1px solid #ddd",
                                                borderRadius: "4px",
                                                backgroundColor: "white",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <option value="bar">Bar Chart</option>
                                            <option value="line">Line Chart</option>
                                            {/* <option value="pie">Pie Chart</option> */}
                                            <option value="doughnut">Doughnut Chart</option>
                                        </select>
                                    </div>
                                </div>

                                <div style={{
                                    flex: 1,
                                    minHeight: 0,
                                    width: "100%",
                                    overflow: "hidden",
                                    position: "relative"
                                }}>
                                    <div style={{
                                        width: "100%",
                                        height: "100%",
                                        minWidth: "100%"
                                    }}>
                                        {/* Dynamic Chart Component */}
                                        {chartType === 'bar' && (
                                            <Bar
                                                data={chartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'top',
                                                        },
                                                        tooltip: {
                                                            mode: 'nearest',
                                                            intersect: false,
                                                            callbacks: {
                                                                label: function (context) {
                                                                    let label = context.dataset.label || '';
                                                                    if (label) {
                                                                        label += ': ';
                                                                    }
                                                                    if (context.parsed.y !== null) {
                                                                        label += new Intl.NumberFormat().format(context.parsed.y);
                                                                    }
                                                                    return label;
                                                                }
                                                            }
                                                        }
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            title: {
                                                                display: true,
                                                                text: 'Current Stock'
                                                            }
                                                        },
                                                        x: {
                                                            title: {
                                                                display: true,
                                                                text: 'SKU'
                                                            },
                                                            ticks: {
                                                                maxRotation: 45,
                                                                minRotation: 45
                                                            }
                                                        }
                                                    },
                                                    interaction: {
                                                        mode: 'nearest',
                                                        axis: 'x',
                                                        intersect: false
                                                    }
                                                }}
                                                height={400}
                                            />
                                        )}

                                        {chartType === 'line' && (
                                            <Line
                                                data={{
                                                    ...chartData,
                                                    datasets: chartData.datasets.map(dataset => ({
                                                        ...dataset,
                                                        borderColor: '#BA386E',
                                                        backgroundColor: 'rgba(186, 56, 110, 0.1)',
                                                        borderWidth: 3,
                                                        tension: 0.4,
                                                        fill: false,
                                                        pointBackgroundColor: '#BA386E',
                                                        pointBorderColor: '#fff',
                                                        pointBorderWidth: 2,
                                                        pointRadius: 4,
                                                        pointHoverRadius: 6
                                                    }))
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'top',
                                                        },
                                                        tooltip: {
                                                            mode: 'nearest',
                                                            intersect: false,
                                                            callbacks: {
                                                                label: function (context) {
                                                                    let label = context.dataset.label || '';
                                                                    if (label) {
                                                                        label += ': ';
                                                                    }
                                                                    if (context.parsed.y !== null) {
                                                                        label += new Intl.NumberFormat().format(context.parsed.y);
                                                                    }
                                                                    return label;
                                                                }
                                                            }
                                                        }
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            title: {
                                                                display: true,
                                                                text: 'Current Stock'
                                                            }
                                                        },
                                                        x: {
                                                            title: {
                                                                display: true,
                                                                text: 'SKU'
                                                            },
                                                            ticks: {
                                                                maxRotation: 45,
                                                                minRotation: 45
                                                            }
                                                        }
                                                    },
                                                    interaction: {
                                                        mode: 'nearest',
                                                        axis: 'x',
                                                        intersect: false
                                                    }
                                                }}
                                                height={400}
                                            />
                                        )}

                                        {/* {chartType === 'pie' && (
                                            <Pie
                                                data={chartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'top',
                                                        },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function (context) {
                                                                    let label = context.label || '';
                                                                    if (label) {
                                                                        label += ': ';
                                                                    }
                                                                    if (context.parsed !== null) {
                                                                        label += new Intl.NumberFormat().format(context.parsed);
                                                                    }
                                                                    return label;
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                                height={400}
                                            />
                                        )} */}

                                        {chartType === 'doughnut' && (
                                            <Doughnut
                                                data={chartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'top',
                                                        },
                                                        tooltip: {
                                                            callbacks: {
                                                                label: function (context) {
                                                                    let label = context.label || '';
                                                                    if (label) {
                                                                        label += ': ';
                                                                    }
                                                                    if (context.parsed !== null) {
                                                                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                                        const percentage = ((context.parsed / total) * 100).toFixed(1);
                                                                        label += `${new Intl.NumberFormat().format(context.parsed)} (${percentage}%)`;
                                                                    }
                                                                    return label;
                                                                }
                                                            }
                                                        }
                                                    },
                                                    elements: {
                                                        arc: {
                                                            borderWidth: 2,
                                                            borderColor: '#fff'
                                                        }
                                                    }
                                                }}
                                                plugins={[{
                                                    id: 'doughnutLabels',
                                                    beforeDraw: function (chart) {
                                                        const ctx = chart.ctx;
                                                        const width = chart.width;
                                                        const height = chart.height;

                                                        ctx.restore();

                                                        // Calculate total
                                                        const total = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);

                                                        // Set text alignment and style
                                                        ctx.textAlign = 'center';
                                                        ctx.textBaseline = 'middle';
                                                        ctx.font = '16px Arial';
                                                        ctx.fillStyle = '#666';

                                                        // Display total in center
                                                        const centerX = width / 2;
                                                        const centerY = height / 2;
                                                        ctx.fillText('Total: ' + new Intl.NumberFormat().format(total), centerX, centerY);

                                                        ctx.save();
                                                    },
                                                    afterDraw: function (chart) {
                                                        const ctx = chart.ctx;
                                                        const meta = chart.getDatasetMeta(0);

                                                        // Calculate total for percentages
                                                        const total = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);

                                                        meta.data.forEach((arc, i) => {
                                                            // Show all segments regardless of size
                                                            const percentage = (chart.data.datasets[0].data[i] / total) * 100;

                                                            // Get arc position
                                                            const startAngle = arc.startAngle;
                                                            const endAngle = arc.endAngle;
                                                            const angle = startAngle + (endAngle - startAngle) / 2;

                                                            // Calculate position for label (outside the chart)
                                                            const radius = arc.outerRadius * 1.1; // Position outside the doughnut
                                                            const x = arc.x + Math.cos(angle) * radius;
                                                            const y = arc.y + Math.sin(angle) * radius;

                                                            // Draw callout line
                                                            const lineLength = 20;
                                                            const lineX = arc.x + Math.cos(angle) * (arc.outerRadius + lineLength / 2);
                                                            const lineY = arc.y + Math.sin(angle) * (arc.outerRadius + lineLength / 2);

                                                            ctx.save();
                                                            ctx.beginPath();
                                                            ctx.moveTo(arc.x + Math.cos(angle) * arc.outerRadius, arc.y + Math.sin(angle) * arc.outerRadius);
                                                            ctx.lineTo(lineX, lineY);
                                                            ctx.strokeStyle = '#666';
                                                            ctx.lineWidth = 1;
                                                            ctx.stroke();

                                                            // Draw percentage label ONLY (no SKU code)
                                                            ctx.textAlign = Math.cos(angle) > 0 ? 'left' : 'right';
                                                            ctx.textBaseline = 'middle';
                                                            ctx.fillStyle = '#333';
                                                            ctx.font = 'bold 12px Arial';

                                                            const labelX = lineX + (Math.cos(angle) > 0 ? 5 : -5);
                                                            const percentageText = `${percentage.toFixed(1)}%`;

                                                            // Draw only percentage text (removed SKU code)
                                                            ctx.fillText(percentageText, labelX, lineY);

                                                            ctx.restore();
                                                        });
                                                    }
                                                }]}
                                                height={400}
                                            />
                                        )}

                                    </div>
                                </div>

                                {/* Rest of your pagination code remains the same */}
                                {chartData && chartData.totalPages > 1 && (
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: "15px",
                                        marginTop: "15px",
                                        padding: "10px",
                                        borderRadius: "5px"
                                    }}>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                                            disabled={currentPage === 0}
                                            style={{
                                                padding: "8px 16px",
                                                cursor: currentPage === 0 ? "not-allowed" : "pointer",
                                                backgroundColor: currentPage === 0 ? "#ccc" : "#BA386E",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                fontWeight: "500"
                                            }}
                                        >
                                            Previous
                                        </button>
                                        <span style={{
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            color: "#333"
                                        }}>
                                            Page {chartData.currentPage} of {chartData.totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(chartData.totalPages - 1, prev + 1))}
                                            disabled={currentPage >= chartData.totalPages - 1}
                                            style={{
                                                padding: "8px 16px",
                                                cursor: currentPage >= chartData.totalPages - 1 ? "not-allowed" : "pointer",
                                                backgroundColor: currentPage >= chartData.totalPages - 1 ? "#ccc" : "#BA386E",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                fontWeight: "500"
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Show No Data Available message when no chart data */
                            <div className="card" style={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                                <div
                                    className="card-body"
                                    style={{
                                        height: "500px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center"
                                    }}
                                >
                                    <div style={{ fontSize: "48px", color: "#dee2e6", marginBottom: "15px" }}>
                                        📊
                                    </div>
                                    <h5 style={{ color: "#6c757d", marginBottom: "10px" }}>
                                        No Data Available
                                    </h5>
                                    <p style={{ color: "#6c757d", fontSize: "14px" }}>
                                        No stock data available for the selected counter on <strong>{stnDate}</strong>
                                    </p>
                                    <p style={{ color: "#6c757d", fontSize: "13px" }}>
                                        Please try selecting a different date
                                    </p>
                                </div>
                            </div>
                        )
                    ) : (
                        /* Grid View - Full Width */
                        filteredRowData && Array.isArray(filteredRowData) && filteredRowData.length > 0 ? (<div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
                            <AgGridReact
                                rowData={filteredRowData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                domLayout="normal"
                                pagination={true}
                                paginationPageSize={filteredRowData.length}
                                enableRangeSelection={true}
                                suppressFieldDotNotation={true}
                                overlayNoRowsTemplate="No data available"
                                sideBar={sideBar}
                                statusBar={statusPanel}
                                onGridReady={(params) => {
                                    if (params && params.api) {
                                        const allColumnIds = params.columnApi.getColumns().map(column => column.getId());
                                        params.columnApi.autoSizeColumns(allColumnIds);
                                    }
                                }}
                                onFirstDataRendered={(params) => {
                                    if (params && params.api) {
                                        const allColumnIds = params.columnApi.getColumns().map(column => column.getId());
                                        params.columnApi.autoSizeColumns(allColumnIds);
                                    }
                                }}
                            />
                        </div>
                        ) : (
                            <div className="card" style={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                                <div
                                    className="card-body"
                                    style={{
                                        height: "500px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center"
                                    }}
                                >
                                    <div style={{ fontSize: "48px", color: "#dee2e6", marginBottom: "15px" }}>
                                        📊
                                    </div>
                                    <h5 style={{ color: "#6c757d", marginBottom: "10px" }}>
                                        No Data Available
                                    </h5>
                                    <p style={{ color: "#6c757d", fontSize: "14px" }}>
                                        No stock data available for the selected counter on <strong>{stnDate}</strong>
                                    </p>
                                    <p style={{ color: "#6c757d", fontSize: "13px" }}>
                                        Please try selecting a different date
                                    </p>
                                </div>
                            </div>
                        )
                    )
                ) : (
                    /* Show No Data Available when no counters are selected AND no counters have data */
                    (!darkstoreCounters.length && !warehouseCounters.length) ? (
                        <div className="row">
                            <div className="col-sm-12 p-0 pl-2 pr-2 mt-4">
                                <div className="card" style={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                                    <div
                                        className="card-body"
                                        style={{
                                            height: "300px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center"
                                        }}
                                    >
                                        <div style={{ fontSize: "48px", color: "#dee2e6", marginBottom: "15px" }}>
                                            📊
                                        </div>
                                        <h5 style={{ color: "#6c757d", marginBottom: "10px" }}>
                                            No Data Available
                                        </h5>
                                        <p style={{ color: "#6c757d", fontSize: "14px" }}>
                                            No stock data found for <strong>{stnDate}</strong>
                                        </p>
                                        <p style={{ color: "#6c757d", fontSize: "13px" }}>
                                            Please try selecting a different date
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Show prompt to select a counter when counters exist but none selected */
                        <div className="row">
                            <div className="col-sm-12 p-0 pl-2 pr-2 mt-4">
                                <div className="card" style={{ border: "1px solid #e0e0e0", borderRadius: "8px" }}>
                                    <div
                                        className="card-body"
                                        style={{
                                            height: "200px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center"
                                        }}
                                    >
                                        <div style={{ fontSize: "48px", color: "#dee2e6", marginBottom: "15px" }}>
                                            👆
                                        </div>
                                        <h5 style={{ color: "#6c757d", marginBottom: "10px" }}>
                                            Select a Counter
                                        </h5>
                                        <p style={{ color: "#6c757d", fontSize: "14px" }}>
                                            Please select a darkstore or warehouse counter above to view detailed data
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                )}
            </div>

            <div className={`loader ${loadshow}`} style={{ zIndex: "1002" }}></div>
<div style={{ zIndex: 997 }} className={`overlay-block ${overlayBlock}`}></div>
        </>
    );
};

export default CurrentStockDashboard;